/**
 * Finanzas → Informes
 * Vista dedicada con IVA trimestral, Modelo 347, y export a Excel.
 * Reutiliza endpoints existentes:
 *   - GET  /api/contabilidad/informes/iva-trimestral?trimestre=N&año=YYYY
 *   - GET  /api/contabilidad/informes/modelo-347?año=YYYY
 *   - GET  /api/contabilidad/export-excel?desde=YYYY-MM-DD&hasta=YYYY-MM-DD
 */
import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Download, FileSpreadsheet, FileText, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/lib/api';
import FinanzasNav from '@/components/finanzas/FinanzasNav';

const fmtEUR = (n) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);

const AÑO_ACTUAL = new Date().getFullYear();
const TRIMESTRE_ACTUAL = Math.floor(new Date().getMonth() / 3) + 1;

export default function FinanzasInformes() {
  const [trimestre, setTrimestre] = useState(String(TRIMESTRE_ACTUAL));
  const [año, setAño] = useState(String(AÑO_ACTUAL));
  const [añoModelo347, setAñoModelo347] = useState(String(AÑO_ACTUAL));
  const [desdeExport, setDesdeExport] = useState(`${AÑO_ACTUAL}-01-01`);
  const [hastaExport, setHastaExport] = useState(`${AÑO_ACTUAL}-12-31`);

  const [ivaData, setIvaData] = useState(null);
  const [m347Data, setM347Data] = useState(null);
  const [loading, setLoading] = useState('');

  const loadIVA = async () => {
    setLoading('iva');
    try {
      const { data } = await API.get(`/contabilidad/informes/iva-trimestral?trimestre=${trimestre}&año=${año}`);
      setIvaData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando IVA');
    } finally {
      setLoading('');
    }
  };

  const loadM347 = async () => {
    setLoading('m347');
    try {
      const { data } = await API.get(`/contabilidad/informes/modelo-347?año=${añoModelo347}`);
      setM347Data(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando Modelo 347');
    } finally {
      setLoading('');
    }
  };

  const downloadExcel = async () => {
    setLoading('excel');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contabilidad/export-excel?desde=${desdeExport}&hasta=${hastaExport}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error('Error al generar Excel');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contabilidad_${desdeExport}_${hastaExport}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading('');
    }
  };

  const años = Array.from({ length: 5 }, (_, i) => AÑO_ACTUAL - i);

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6" data-testid="finanzas-informes">
      <FinanzasNav />
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileText className="w-7 h-7 text-primary" />
          Informes fiscales
        </h1>
        <p className="text-sm text-muted-foreground mt-1">IVA trimestral, Modelo 347 y export contable</p>
      </div>

      <Tabs defaultValue="iva" className="space-y-4">
        <TabsList>
          <TabsTrigger value="iva" data-testid="tab-iva">IVA Trimestral</TabsTrigger>
          <TabsTrigger value="m347" data-testid="tab-m347">Modelo 347</TabsTrigger>
          <TabsTrigger value="excel" data-testid="tab-excel">Export Excel</TabsTrigger>
        </TabsList>

        {/* ============== IVA TRIMESTRAL ============== */}
        <TabsContent value="iva" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Resumen IVA repercutido vs soportado</CardTitle>
              <CardDescription>Modelo 303 trimestral</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Trimestre</Label>
                  <Select value={trimestre} onValueChange={setTrimestre}>
                    <SelectTrigger className="w-32" data-testid="iva-trimestre">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">T1 (Ene-Mar)</SelectItem>
                      <SelectItem value="2">T2 (Abr-Jun)</SelectItem>
                      <SelectItem value="3">T3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4">T4 (Oct-Dic)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Año</Label>
                  <Select value={año} onValueChange={setAño}>
                    <SelectTrigger className="w-28" data-testid="iva-año">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {años.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadIVA} disabled={loading === 'iva'} data-testid="btn-load-iva">
                  {loading === 'iva' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Calcular
                </Button>
              </div>

              {ivaData && (
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="pt-5">
                      <p className="text-xs uppercase text-emerald-700 mb-1">IVA Repercutido</p>
                      <p className="text-2xl font-bold">{fmtEUR(ivaData.iva_repercutido)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Base imponible: {fmtEUR(ivaData.base_repercutido)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-5">
                      <p className="text-xs uppercase text-amber-700 mb-1">IVA Soportado</p>
                      <p className="text-2xl font-bold">{fmtEUR(ivaData.iva_soportado)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Base imponible: {fmtEUR(ivaData.base_soportado)}</p>
                    </CardContent>
                  </Card>
                  <Card className={(ivaData.iva_a_ingresar || 0) > 0 ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-300'}>
                    <CardContent className="pt-5">
                      <p className="text-xs uppercase text-blue-700 mb-1">A ingresar / devolver</p>
                      <p className="text-2xl font-bold">{fmtEUR(ivaData.iva_a_ingresar || ivaData.diferencia)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(ivaData.iva_a_ingresar || 0) > 0 ? 'A pagar a Hacienda' : 'Devolución'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== MODELO 347 ============== */}
        <TabsContent value="m347" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Modelo 347 — Operaciones &gt; 3.005,06 €</CardTitle>
              <CardDescription>Declaración anual de operaciones con terceros</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Año</Label>
                  <Select value={añoModelo347} onValueChange={setAñoModelo347}>
                    <SelectTrigger className="w-28" data-testid="m347-año">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {años.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={loadM347} disabled={loading === 'm347'} data-testid="btn-load-m347">
                  {loading === 'm347' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Calcular
                </Button>
              </div>

              {m347Data && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">Total operaciones: {m347Data.total_operaciones || 0}</Badge>
                    <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                      Importe total: {fmtEUR(m347Data.total_importe)}
                    </Badge>
                  </div>
                  {(m347Data.declarables || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin operaciones declarables (todas &lt; 3.005,06 €)</p>
                  ) : (
                    <div className="space-y-1">
                      {(m347Data.declarables || []).map((d, i) => (
                        <div key={`${d.nif}-${i}`} className="flex justify-between items-center p-3 hover:bg-muted/50 rounded border">
                          <div>
                            <p className="text-sm font-medium">{d.nombre || '(sin nombre)'}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{d.nif || '—'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold">{fmtEUR(d.importe)}</p>
                            <p className="text-[10px] text-muted-foreground">{d.tipo || 'compras'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============== EXPORT EXCEL ============== */}
        <TabsContent value="excel" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Exportar a Excel
              </CardTitle>
              <CardDescription>Libro Excel con hojas de facturas, albaranes, pagos y resumen por cliente</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-3 max-w-xl">
                <div className="space-y-1">
                  <Label htmlFor="desde" className="text-xs">Desde</Label>
                  <Input id="desde" type="date" value={desdeExport} onChange={(e) => setDesdeExport(e.target.value)} data-testid="export-desde" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="hasta" className="text-xs">Hasta</Label>
                  <Input id="hasta" type="date" value={hastaExport} onChange={(e) => setHastaExport(e.target.value)} data-testid="export-hasta" />
                </div>
                <Button onClick={downloadExcel} disabled={loading === 'excel'} className="mt-5" data-testid="btn-download-excel">
                  {loading === 'excel' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Descargar
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
